import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AiCampaignDraft,
  AiCampaignDraftStatus,
  AiDraftRequest,
  generateAiDraft,
  getAiDraftStatus,
} from '../api/aiDraftApi';

/**
 * The campaign composer fields the assistant can populate. Mirrors
 * {@code CampaignComposerValue} but kept structurally independent
 * so this component drops cleanly into the legacy ContactEmailPage
 * (which doesn't share that type) without forcing a refactor.
 */
export interface AiCampaignAssistantDraft {
  subject?: string;
  preheader?: string;
  bodyText?: string;
  ctaText?: string;
  ctaPath?: string;
  fromAddress?: string;
}

interface AiCampaignAssistantProps {
  /** Live composer state. Determines whether the assistant generates from scratch or refines. */
  currentDraft?: AiCampaignAssistantDraft;
  /**
   * Apply a generated draft back to the parent form. The assistant
   * passes only the fields the model returned — callers should
   * merge against their existing state rather than overwrite, so
   * empty fields from a refine call don't clobber what the admin
   * typed by hand.
   */
  onApply: (draft: AiCampaignAssistantDraft) => void;
  /** Human label for the audience the draft is targeting (e.g. "churned_bronze"). Surfaced to the model. */
  audienceHint?: string;
  /** Live recipient count, when known. Helps the model size urgency vs warmth. */
  audienceSize?: number;
  /** Renders inside a parent container that already provides padding/border — defaults to true (standalone card). */
  standalone?: boolean;
  /** Disable all controls (e.g. while the parent is launching). */
  disabled?: boolean;
}

/**
 * Quick-start chips. These map to the conversion "plays" the drafter
 * can run (it picks the play via tools server-side, so a chip just
 * nudges it toward the right lever and audience). The first two are
 * reply-goal plays: the founder grants trials and coaching by hand in
 * reply to the email, so the conversion event is a reply, not a click.
 * Prompts use plain commas and periods (no em dashes) so we don't seed
 * the model with the AI tic we just told it to avoid.
 */
const IDEA_CHIPS: { label: string; prompt: string }[] = [
  {
    label: 'Offer a personal trial',
    prompt:
      "Warm free users who haven't upgraded. Run the founder-personal-trial play: Marshall personally offers to set them up with a free trial if they reply. Ask one small question to make replying easy. Reply CTA, no subscribe link.",
  },
  {
    label: 'Offer 1:1 coaching',
    prompt:
      "New or stuck free users. Run the concierge-coaching play: Marshall offers to personally help them find their first edge. Invite a reply with their biggest betting question. Warm, zero pressure, reply CTA.",
  },
  {
    label: 'Member proof',
    prompt:
      "Cold leads or free users who never upgraded. Run the member-proof play: lead with a believable member result and observed ROI framed honestly, then point to tonight's edge. CTA to the +EV finder.",
  },
  {
    label: 'Cross-sell a tool',
    prompt:
      "Active users on a lower tier. Run the feature-discovery play: surface one tool they probably aren't using (parlay builder on Silver, live in-play on Gold) and the edge it unlocks. Use the real route or /pricing if gated.",
  },
  {
    label: 'Win-back lapsed',
    prompt:
      "Lapsed or dormant users. Run the winback-whats-new play: acknowledge the gap, lead with one concrete new thing, and offer a personal trial if they reply. No discount.",
  },
  {
    label: 'Activate new signups',
    prompt:
      "Signups from the last 7 days who haven't found an edge yet. Run the activation-first-edge play: lower the bar to one action, two sentences on what they'll see and why it matters. CTA to /positive-ev.",
  },
];

const PROMPT_MIN = 8;
const PROMPT_MAX = 1000;

const AiCampaignAssistant: React.FC<AiCampaignAssistantProps> = ({
  currentDraft,
  onApply,
  audienceHint,
  audienceSize,
  standalone = true,
  disabled = false,
}) => {
  const [status, setStatus] = useState<AiCampaignDraftStatus | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastDraft, setLastDraft] = useState<AiCampaignDraft | null>(null);
  const [applied, setApplied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const s = await getAiDraftStatus();
      if (mounted) {
        setStatus(s);
        setStatusLoaded(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Refine-mode threshold: any meaningful body or subject already
  // present in the composer flips the assistant from "draft" to
  // "refine". The button label + placeholder change accordingly so
  // it's obvious the model will iterate, not overwrite.
  const isRefining = useMemo(() => {
    const d = currentDraft;
    if (!d) return false;
    return (
      (d.subject?.trim().length ?? 0) > 0 ||
      (d.bodyText?.trim().length ?? 0) > 0
    );
  }, [currentDraft]);

  const promptValid = prompt.trim().length >= PROMPT_MIN;
  const canSubmit = promptValid && !busy && !disabled && (status?.enabled ?? false);

  const handleGenerate = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setApplied(false);
    try {
      const req: AiDraftRequest = { prompt: prompt.trim() };
      if (audienceHint) req.audienceHint = audienceHint;
      if (audienceSize && audienceSize > 0) req.audienceSize = audienceSize;
      if (isRefining && currentDraft) {
        req.currentDraft = {
          subject: currentDraft.subject || undefined,
          preheader: currentDraft.preheader || undefined,
          bodyText: currentDraft.bodyText || undefined,
          ctaText: currentDraft.ctaText || undefined,
          ctaPath: currentDraft.ctaPath || undefined,
          fromAddress: currentDraft.fromAddress || undefined,
        };
      }
      const draft = await generateAiDraft(req);
      setLastDraft(draft);
      setExpanded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate draft');
    } finally {
      setBusy(false);
    }
  };

  // We apply whatever the admin has in front of them — the editable
  // DraftPreview owns the canonical "about to be applied" state, so
  // we accept it as the source of truth here. Empty strings collapse
  // to undefined so we never overwrite a perfectly good composer
  // field with a blank the admin cleared mid-edit.
  const handleApply = (edited: AiCampaignDraft) => {
    const out: AiCampaignAssistantDraft = {};
    const subject = edited.subject?.trim();
    const preheader = edited.preheader?.trim();
    const bodyText = edited.bodyText?.trim();
    const ctaText = edited.ctaText?.trim();
    const ctaPath = edited.ctaPath?.trim();
    const fromAddress = edited.fromAddress?.trim();
    if (subject) out.subject = subject;
    if (preheader) out.preheader = preheader;
    if (bodyText) out.bodyText = bodyText;
    if (ctaText) out.ctaText = ctaText;
    if (ctaPath) out.ctaPath = ctaPath;
    if (fromAddress) out.fromAddress = fromAddress;
    onApply(out);
    setApplied(true);
    setPrompt('');
  };

  const handleChip = (chipPrompt: string) => {
    setPrompt(chipPrompt);
    setApplied(false);
    setTimeout(() => taRef.current?.focus(), 0);
  };

  if (statusLoaded && status && !status.enabled) {
    // Surface the not-configured state inline so the admin knows
    // why the panel is greyed out instead of guessing the button
    // is broken.
    return (
      <Wrapper standalone={standalone}>
        <HeaderRow title="AI campaign draft" />
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
          AI drafting is not configured on this server. Ask an admin to set{' '}
          <code className="text-[10px]">OPENAI_API_KEY</code> to enable.
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper standalone={standalone}>
      <HeaderRow
        title={isRefining ? 'Refine with AI' : 'Draft with AI'}
        model={status?.model ?? undefined}
        audienceHint={audienceHint}
        audienceSize={audienceSize}
      />

      <div className="space-y-2">
        <textarea
          ref={taRef}
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            if (applied) setApplied(false);
          }}
          rows={isRefining ? 2 : 3}
          maxLength={PROMPT_MAX}
          disabled={disabled || busy}
          placeholder={
            isRefining
              ? "e.g. make it shorter, swap the CTA to /live, less salesy"
              : "Describe the campaign in one or two sentences \u2014 audience, angle, the CTA you want."
          }
          className="w-full px-3 py-2 text-sm text-gray-900 placeholder-gray-400 border border-indigo-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none leading-relaxed"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              if (canSubmit) handleGenerate();
            }
          }}
        />

        {!isRefining && (
          <div className="flex flex-wrap gap-1.5">
            {IDEA_CHIPS.map((c) => (
              <button
                key={c.label}
                type="button"
                onClick={() => handleChip(c.prompt)}
                disabled={disabled || busy}
                className="text-[11px] px-2 py-1 rounded-full border border-[#94c2dc]/70 text-[#0b3a55] bg-white hover:bg-[#eef7fb] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-gray-400">
            {prompt.length}/{PROMPT_MAX} · ⌘⏎ to generate
          </span>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!canSubmit}
            className="px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-b from-[#01183a] to-[#09264f] rounded-md shadow-sm hover:from-[#09264f] hover:to-[#0b3a55] disabled:from-slate-200 disabled:to-slate-300 disabled:text-slate-600 disabled:cursor-not-allowed disabled:shadow-none"
            title={
              !status?.enabled
                ? 'AI not configured'
                : !promptValid
                  ? `Type at least ${PROMPT_MIN} characters`
                  : isRefining
                    ? 'Refine the current draft'
                    : 'Generate a new draft'
            }
          >
            {busy
              ? isRefining
                ? 'Refining\u2026'
                : 'Drafting\u2026'
              : isRefining
                ? 'Refine'
                : 'Generate'}
          </button>
        </div>

        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        {lastDraft && expanded && (
          <DraftPreview
            draft={lastDraft}
            applied={applied}
            onApply={handleApply}
            onCollapse={() => setExpanded(false)}
            onRegenerate={() => {
              setExpanded(false);
              setLastDraft(null);
              setApplied(false);
              setTimeout(() => taRef.current?.focus(), 0);
            }}
          />
        )}
      </div>
    </Wrapper>
  );
};

// ── presentational helpers ─────────────────────────────────────────────

const Wrapper: React.FC<{ standalone: boolean; children: React.ReactNode }> = ({
  standalone,
  children,
}) =>
  standalone ? (
    <div className="bg-gradient-to-br from-[#eef7fb] via-white to-white border border-[#94c2dc]/40 rounded-xl p-4 space-y-3 shadow-sm">
      {children}
    </div>
  ) : (
    <div className="space-y-3">{children}</div>
  );

const HeaderRow: React.FC<{
  title: string;
  model?: string;
  audienceHint?: string;
  audienceSize?: number;
}> = ({ title, model, audienceHint, audienceSize }) => (
  <div className="flex items-start justify-between gap-2 flex-wrap">
    <div className="flex items-center gap-2 min-w-0">
      <SparkleIcon />
      <div>
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        {audienceHint && (
          <div className="text-[11px] text-gray-500 truncate">
            for <span className="font-medium text-gray-700">{audienceHint}</span>
            {audienceSize && audienceSize > 0 && (
              <span className="text-gray-400">
                {' '}
                · {audienceSize.toLocaleString()} recipients
              </span>
            )}
          </div>
        )}
      </div>
    </div>
    {model && (
      <span className="text-[10px] text-indigo-700 bg-indigo-100 border border-indigo-200 rounded-full px-2 py-0.5 font-medium">
        {model}
      </span>
    )}
  </div>
);

const SparkleIcon: React.FC = () => (
  <svg
    className="h-4 w-4 text-indigo-600 flex-shrink-0"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M12 2l1.7 4.6L18 8l-4.3 1.7L12 14l-1.7-4.3L6 8l4.3-1.4L12 2zm6 10l1 2.5L21 15l-2 1-1 2-1-2-2-1 2-1 1-2zm-12 4l1 2.5L9 19l-2 1-1 2-1-2-2-1 2-1 1-2z" />
  </svg>
);

interface DraftPreviewProps {
  draft: AiCampaignDraft;
  applied: boolean;
  onApply: (edited: AiCampaignDraft) => void;
  onCollapse: () => void;
  onRegenerate: () => void;
}

/**
 * Editable preview of the AI-generated draft. The model writes the
 * first pass — the admin tweaks subject lines, bodies, and CTAs
 * inline before clicking "Apply to form". We hold the edit state
 * here (rather than threading it through the parent) because:
 *
 *  - "Try another" should never silently nuke the admin's edits —
 *    we collapse the preview, which throws this component's state
 *    away, on purpose.
 *  - "Reset to AI version" gives a one-click bail-out without
 *    re-prompting the model, which costs a real API call.
 *  - The parent composer is the canonical "what will be sent"
 *    state once Apply runs; until then this is a scratchpad.
 */
const DraftPreview: React.FC<DraftPreviewProps> = ({
  draft,
  applied,
  onApply,
  onCollapse,
  onRegenerate,
}) => {
  const [edited, setEdited] = useState<AiCampaignDraft>(() => ({ ...draft }));

  // The parent re-mounts this component on every new generation
  // (key={lastDraft.subject + ...} would be brittle), so we sync
  // when the draft reference changes — that's the "Try another"
  // case where a fresh draft replaces the old one.
  useEffect(() => {
    setEdited({ ...draft });
  }, [draft]);

  const isDirty = useMemo(() => {
    const fields: (keyof AiCampaignDraft)[] = [
      'subject',
      'preheader',
      'bodyText',
      'ctaText',
      'ctaPath',
      'fromAddress',
    ];
    return fields.some((f) => (edited[f] ?? '') !== (draft[f] ?? ''));
  }, [edited, draft]);

  const setField = (key: keyof AiCampaignDraft, value: string) => {
    setEdited((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="bg-white border border-indigo-200 rounded-lg p-3 space-y-2.5 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-indigo-700">
            Draft ready
          </span>
          {isDirty && (
            <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
              Edited
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <button
              type="button"
              onClick={() => setEdited({ ...draft })}
              className="text-[11px] text-gray-400 hover:text-gray-700"
              title="Revert your edits to the AI version"
            >
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={onCollapse}
            className="text-[11px] text-gray-400 hover:text-gray-700"
          >
            Hide
          </button>
        </div>
      </div>

      <EditableField label="Subject">
        <input
          type="text"
          value={edited.subject ?? ''}
          onChange={(e) => setField('subject', e.target.value)}
          maxLength={200}
          className="w-full px-2 py-1.5 text-[13px] text-gray-900 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        />
      </EditableField>

      <EditableField label="Preheader">
        <input
          type="text"
          value={edited.preheader ?? ''}
          onChange={(e) => setField('preheader', e.target.value)}
          maxLength={200}
          className="w-full px-2 py-1.5 text-[13px] text-gray-700 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        />
      </EditableField>

      <EditableField
        label="Body"
        hint="plain text or light HTML; greeting and sign-off auto-added"
      >
        <textarea
          value={edited.bodyText ?? ''}
          onChange={(e) => setField('bodyText', e.target.value)}
          rows={8}
          className="w-full px-2 py-1.5 text-[12.5px] text-gray-800 leading-relaxed border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 font-sans resize-y"
        />
      </EditableField>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-2">
        <EditableField label="CTA text">
          <input
            type="text"
            value={edited.ctaText ?? ''}
            onChange={(e) => setField('ctaText', e.target.value)}
            className="w-full px-2 py-1.5 text-[13px] text-gray-900 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
          />
        </EditableField>
        <EditableField label="CTA link">
          <input
            type="text"
            value={edited.ctaPath ?? ''}
            onChange={(e) => setField('ctaPath', e.target.value)}
            className="w-full px-2 py-1.5 text-[12px] font-mono text-gray-800 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
          />
        </EditableField>
      </div>

      {(edited.fromAddress || draft.fromAddress) && (
        <EditableField label="From">
          <input
            type="text"
            value={edited.fromAddress ?? ''}
            onChange={(e) => setField('fromAddress', e.target.value)}
            placeholder="leave blank for the default sender"
            className="w-full px-2 py-1.5 text-[12px] font-mono text-gray-800 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
          />
        </EditableField>
      )}

      {(draft.strategy || (draft.playsUsed && draft.playsUsed.length > 0)) && (
        <div className="text-[11px] text-[#0b3a55] bg-[#eef7fb] border border-[#94c2dc]/50 rounded px-2 py-1.5 space-y-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] uppercase tracking-wide font-semibold text-[#0b3a55]/70">
              Play
            </span>
            {draft.playsUsed?.map((p) => (
              <span
                key={p}
                className="text-[10px] font-medium bg-white border border-[#94c2dc]/60 text-[#0b3a55] rounded-full px-1.5 py-0.5"
              >
                {p}
              </span>
            ))}
          </div>
          {draft.strategy && <div className="leading-snug">{draft.strategy}</div>}
        </div>
      )}

      {draft.rationale && (
        <div className="text-[11px] text-indigo-700 bg-indigo-50/60 border border-indigo-100 rounded px-2 py-1.5 italic">
          Why this should convert: {draft.rationale}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onRegenerate}
          className="px-3 py-1.5 text-xs border border-gray-200 text-gray-700 rounded-md hover:bg-gray-50"
        >
          Try another
        </button>
        <button
          type="button"
          onClick={() => onApply(edited)}
          disabled={applied}
          className={`px-3 py-1.5 text-xs font-medium rounded-md shadow-sm ${
            applied
              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-default'
              : 'text-white bg-gradient-to-b from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800'
          }`}
        >
          {applied ? 'Applied' : 'Apply to form'}
        </button>
      </div>
    </div>
  );
};

const EditableField: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className="text-xs">
    <div className="flex items-baseline justify-between mb-0.5 gap-2">
      <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        {label}
      </span>
      {hint && <span className="text-[10px] text-gray-400 text-right">{hint}</span>}
    </div>
    {children}
  </div>
);

export default AiCampaignAssistant;
